/**
 * Stub local DB module — ADR-017 Fase 4 cleanup.
 *
 * SQLite was eliminated as an operational data path. All platforms now use
 * RAM cache + Supabase online (see ADR-017). This file provides stub
 * implementations of the symbols previously exported from `shared/db/`,
 * so that consumers continue to compile and run without SQLite.
 *
 * - `db` is a no-op stub: getAllAsync → [], getFirstAsync → null, runAsync → {changes:0,...}, execAsync → void.
 * - `withSqliteTransaction` calls the task with the stub `db`.
 * - `isDatabaseUnavailableError` always returns false (the stub never throws).
 * - Account data functions are no-ops (RAM caches are cleared via `runSessionCacheClearHandlers`).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SqliteRunResult = {
  changes: number;
  lastInsertRowId: number | bigint;
};

export type SqliteAsyncConnection = {
  execAsync: (sql: string) => Promise<void>;
  runAsync: (sql: string, ...params: any[]) => Promise<SqliteRunResult>;
  getAllAsync: <T = any>(sql: string, ...params: any[]) => Promise<T[]>;
  getFirstAsync: <T = any>(sql: string, ...params: any[]) => Promise<T | undefined>;
};

export type ColumnPatch = readonly [table: string, column: string, definition: string];

export type PrepareLocalDataForAccountExitOptions = {
  reason: 'logout' | 'account_switch' | 'account_deletion' | 'organization_switch';
  warnIfPending?: boolean;
};

// ---------------------------------------------------------------------------
// Error types (preserved for catch blocks that reference them)
// ---------------------------------------------------------------------------

export class DatabaseUnavailableError extends Error {
  code = 'DB_UNAVAILABLE_INVALID_VFS' as const;

  constructor(message = 'SQLite is unavailable in this browser session (Invalid VFS state).') {
    super(message);
    this.name = 'DatabaseUnavailableError';
  }
}

export const DATABASE_UNAVAILABLE_USER_MESSAGE =
  'El almacenamiento local del navegador falló o está en uso por otra pestaña. Cerrá el resto de pestañas de Vuelto, recargá esta página y probá de nuevo.';

export const isDatabaseUnavailableError = (err: unknown): err is DatabaseUnavailableError =>
  err instanceof DatabaseUnavailableError ||
  (err instanceof Error && err.name === 'DatabaseUnavailableError');

export const isSqliteFinalizingError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return msg.includes('Error finalizing statement') || msg.includes('finalizing statement');
};

// ---------------------------------------------------------------------------
// Stub db object — no-op, returns empty/null results
// ---------------------------------------------------------------------------

const STUB_RESULT: SqliteRunResult = { changes: 0, lastInsertRowId: 0 };

export const db: SqliteAsyncConnection = {
  execAsync: async () => {},
  runAsync: async () => STUB_RESULT,
  getAllAsync: async () => [],
  getFirstAsync: async () => undefined,
};

// ---------------------------------------------------------------------------
// Transaction / init stubs
// ---------------------------------------------------------------------------

export async function ensureDatabaseInitialized(): Promise<void> {}

export async function withSqliteTransaction<T>(
  task: (conn: SqliteAsyncConnection) => Promise<T>,
): Promise<T> {
  return task(db);
}

export async function dedupeDuplicateStocksRows(): Promise<void> {}

// ---------------------------------------------------------------------------
// Schema helper stubs
// ---------------------------------------------------------------------------

export async function applyColumnPatches(
  _conn: SqliteAsyncConnection,
  _patches: readonly ColumnPatch[],
): Promise<void> {}

// ---------------------------------------------------------------------------
// Outbox guard stubs
// ---------------------------------------------------------------------------

export async function countPendingOrFailedSyncQueue(
  _conn: SqliteAsyncConnection,
): Promise<number> {
  return 0;
}

export async function hasPendingOrFailedSyncQueue(
  conn: SqliteAsyncConnection,
): Promise<boolean> {
  return (await countPendingOrFailedSyncQueue(conn)) > 0;
}

// ---------------------------------------------------------------------------
// Settings repo stubs (previously sqliteSettingsRepo.ts)
// ---------------------------------------------------------------------------

export async function getLocalDataOwnerUserId(): Promise<string | null> {
  return null;
}

export async function setLocalDataOwnerUserId(_userId: string): Promise<void> {}

export async function clearLocalDataOwnerUserId(): Promise<void> {}

// ---------------------------------------------------------------------------
// Account data stubs (previously sqliteAccountData.ts)
// ---------------------------------------------------------------------------

export async function softResetMirrorTables(_conn: SqliteAsyncConnection): Promise<void> {}

export async function wipeKioskOperationalData(_conn: SqliteAsyncConnection): Promise<void> {}

export async function prepareLocalDataForAccountExit(
  _options: PrepareLocalDataForAccountExitOptions,
): Promise<void> {
  // RAM caches are cleared via runSessionCacheClearHandlers in the caller.
}

export async function prepareLocalDataForOrganizationSwitch(
  _options?: Pick<PrepareLocalDataForAccountExitOptions, 'warnIfPending'>,
): Promise<void> {}

export async function ensureLocalDataMatchesAuthUser(_authUserId: string): Promise<void> {}

export async function clearLocalKioskDataForNewAccount(): Promise<void> {}

export async function clearLocalCatalogDataForBusinessTypeChange(): Promise<void> {}

// ---------------------------------------------------------------------------
// Constants (previously from desiredSqliteSchema.ts)
// ---------------------------------------------------------------------------

export const MIN_SUPPORTED_CATALOG_SNAPSHOT_SCHEMA_VERSION = 1;
